import type { Department } from '@recruitee/departments-types';
import type { JobTemplateFull } from '@recruitee/job-templates-types';
import type { Country } from '@recruitee/locations-types';
import type { PaginationState } from '@recruitee/ngrx';
import type { OfferFull, OfferStatus } from '@recruitee/offers-types';

import type { ReferralsPortalApplicationStatus } from './application.types';
import type { OfferAssignmentRefType, RefTypeBase } from './base';
import type {
  ReferralQuestionKind,
  ReferralTermKind,
  TReferralQuestionKind,
} from './referrals.enums';
import type { ReferralReward } from './reward.types';

export type ReferralsSettings = {
  id: number;
  enabled: boolean;
  rewardsVisible: boolean;
  signupDomainsEnabled: boolean;
  referralStatusVisible: boolean;
  connectingAccountsEnabled: boolean;
  cvUploadEnabled: boolean;
  referrersCount: number;
  rewardsCount: number;
  applicationsCount: number;
  subdomain: string;
  signupDomains: string[];
  formSettings: ReferralsFormSettings;
};

export enum FieldSettings {
  Required = 'required',
  Optional = 'optional',
  Hidden = 'hidden',
}

export type ReferralsFormSettings = {
  cv: FieldSettings;
  photo: FieldSettings;
  socialLinks: FieldSettings;
  email: FieldSettings;
};

// remove after referralshub refactor
export interface TableMeta {
  totalCount: number;
  page: number;
  limit: number;
  loaded: boolean;
  pending: boolean;
}

export interface TableMetaNew extends PaginationState {
  loaded: boolean;
  pending: boolean;
}

// remove after referralshub refactor
export interface TableData<T> extends TableMeta {
  rows: T[];
}

export interface TableDataNew<T> extends TableMetaNew {
  rows: T[];
}

export interface TableDataParams {
  limit?: number;
  page?: number;
  q?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface ApplicationsParams extends TableDataParams {
  referrerType: 'admin' | 'external_user';
  status: ReferralsPortalApplicationStatus;
  withReward: boolean;
  claimStatus: ClaimStatus;
}

export enum ClaimStatus {
  Open = 'open',
  Accepted = 'accepted',
  Rejected = 'rejected',
}

export interface ClaimCounter {
  claimStatus: ClaimStatus;
  count: number;
}

export interface Referrer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  photo: string;
  avatarUrl: string;
  avatarThumbUrl: string;
  claimedApplicationsCount: number;
  anonymizedAt: string | null;
}

export interface CandidateReferrersReference extends CandidateReferrer {
  type: 'ReferralReferrer';
}

export interface CandidateReferrer {
  avatarThumbUrl: string;
  email: string;
  firstName: string;
  id: number;
  isAdmin: string;
  lastName: string;
  type: string;
  anonymizedAt: string | null;
}

export interface ReferralTemplateBase extends RefTypeBase<OfferAssignmentRefType.ReferralTemplate> {
  id: number;
  name: string;
  title: string;
  position: string;
  category: string;
  default: boolean;
  custom: boolean;
  createdAt?: string;
  updatedAt?: string;
  assignedOffersCount: number;
  assignedOfferTemplatesCount: number;
}

export interface ReferralTemplate extends ReferralTemplateBase {
  referralQuestions: ReferralQuestion[];
}

export interface ReferralQuestion {
  id: number;
  title: string;
  required: boolean;
  position: number;
  options: Array<{ title: string; position?: string; id?: string | number }>;
  _destroy?: boolean;
  kind: ReferralQuestionKind;
}

export interface ReferralQuestionPosition {
  id: number;
  position: number;
}

export const tableDataDefaultParams: TableDataParams = {
  limit: 10,
};

export interface ReferralsOffer
  extends Pick<
    OfferFull,
    | 'id'
    | 'kind'
    | 'langCode'
    | 'position'
    | 'slug'
    | 'title'
    | 'description'
    | 'requirements'
    | 'location'
    | 'locationIds'
    | 'locations'
    | 'department'
    | 'status'
    | 'guid'
  > {
  status: OfferStatus.Published | OfferStatus.Internal;
  dynamicFields?: Array<{
    id: number;
    displayName: string;
    displayValue: string;
  }>;
}

export interface ReferralsJobTemplate
  extends Pick<
    JobTemplateFull,
    | 'id'
    | 'name'
    | 'langCode'
    | 'description'
    | 'requirements'
    | 'requirementsJson'
    | 'locationIds'
    | 'departmentId'
  > {}
export interface ReferralAssignmentBase {
  id: number;
  enabled: boolean;
  referralRewardId: number;
  referralReward: ReferralReward | null;
  referralTemplateId: number;
  referralTemplate: ReferralTemplate | null;
  customTemplateId: number | null;
}
export interface ReferralOfferAssignment extends ReferralAssignmentBase {
  offer: ReferralsOffer;
}

export interface ReferralJobTemplateAssignment extends ReferralAssignmentBase {
  offerTemplate: ReferralsJobTemplate;
}

export interface ReferralsCondition {
  id: number;
  position?: number;
  bodyHtml: string;
}

export interface ReferralsTerm {
  id: number;
  kind: ReferralTermKind;
  linkEnabled: boolean;
  bodyHtml: string;
  sourceLink: string;
}

export interface ReferralTemplate extends ReferralTemplateBase {
  referralQuestions: ReferralQuestion[];
}

export interface ReferralAssigmentBase {
  id: number;
  enabled: true;
  referralRewardId: number;
  referralReward: ReferralReward;
  referralTemplateId: number;
  referralTemplate: ReferralTemplate;
  customTemplateId: number;
  customTemplate: ReferralTemplate;
}

export interface ReferralFilterOptions {
  cities: string[];
  countries: Country[];
  departments: Array<Partial<Department>>;
}

export interface ReferralQuestionAnswers {
  id: number;
  flag?: boolean;
  questionTitle: string;
  questionRequired: boolean;
  questionPosition: number;
  questionKind: TReferralQuestionKind;
  contents: string[];
  content: string;
}

export interface ReferralsApplicationAnswer {
  id: number;
  offerTitle: string;
  createdAt: string;
  referralReferrer: Referrer;
  referralQuestionAnswers: ReferralQuestionAnswers[];
}
